import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { isPrivilegedRole } from '@automatick/shared-ng';
import { Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, take } from 'rxjs/operators';
import { AppStateService, WorkspaceBootstrapState } from '../services';
import {
  hasAuthenticatedWorkspaceContext,
  isTerminalWorkspaceContext,
} from './workspace-access.guard';

type PrivilegedAccessResult = true | UrlTree;

export const privilegedRoleGuard: CanActivateFn = () => {
  const appState = inject(AppStateService);
  const router = inject(Router);
  const rejectedAccess = router.parseUrl('/home');
  const resolveAccess = (
    state: WorkspaceBootstrapState,
  ): PrivilegedAccessResult =>
    hasAuthenticatedWorkspaceContext(state) &&
    isPrivilegedRole(state.activeRole)
      ? true
      : rejectedAccess;

  try {
    const initialState = appState.getStateSnapshot();
    if (isTerminalWorkspaceContext(initialState)) {
      return resolveAccess(initialState);
    }

    if (!initialState.isLoading) {
      appState.bootstrapWorkspaceContext();
    }

    return appState.state$.pipe(
      filter(isTerminalWorkspaceContext),
      take(1),
      map(resolveAccess),
      defaultIfEmpty(rejectedAccess),
      catchError((): Observable<PrivilegedAccessResult> => of(rejectedAccess)),
    );
  } catch {
    return rejectedAccess;
  }
};
